import { ChangeEvent, Children, isValidElement, ReactElement, useId } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { DropdownProps } from 'react-day-picker'
import { createPortal } from 'react-dom'

import { ExpandIcon } from '@arcade/web/icons/react'

import { buttonLikeActive, buttonLikeBase, buttonLikeFocus, buttonLikeSelected } from './ArcadeDatePickerClassNames'
import { usePortalElement } from './DatePickerCaption'
import { Option, useDropdownState } from './DatePickerDropdownContext'

const Dropdown = styled.div`
  ${props => buttonLikeBase(props.theme)}
  ${props => props.theme.typography.body};

  height: ${props => props.theme.pxToRem(44)};
  width: 100%;
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing.xxs};

  :last-of-type {
    margin-left: ${props => props.theme.spacing.s};
  }

  :active {
    ${props => buttonLikeActive(props.theme)}
  }

  @media (hover: hover) {
    :hover {
      background: ${props => props.theme.arcadeColors.semantic.backgroundStandard};
    }
  }

  &[aria-expanded='true'] {
    ${props => buttonLikeSelected(props.theme)}
    ${props => props.theme.typography.body};
    outline: 0;
  }

  ${props => props.theme.selectors.keyboard} &[aria-expanded='false']:focus {
    ${props => buttonLikeFocus(props.theme)}
  }
`

const DropdownLabelContainer = styled.span`
  display: grid;
  justify-items: center;
`

const DropdownLabel = styled.span`
  grid-area: 1/1;
`

const DropdownLabelSpacer = styled.span`
  /* prevents layout from shifting when a bold option is selected */
  grid-area: 1/1;
  visibility: hidden;
  ${props => props.theme.typography.titleBarPageTitle};
`

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-auto-rows: ${props => props.theme.pxToRem(44)};
  gap: ${props => props.theme.spacing.s};
  max-height: 13.5rem;
  overflow-y: auto;
`

const OptionElement = styled.div<{ isActiveDescendent: boolean }>`
  ${props => buttonLikeBase(props.theme)}
  position: relative;

  :active {
    ${props => buttonLikeActive(props.theme)}
  }

  @media (hover: hover) {
    :hover {
      background: ${props => props.theme.arcadeColors.semantic.backgroundStandard};
    }
  }

  &[aria-selected='true'] {
    ${props => buttonLikeSelected(props.theme)}
  }

  ${props =>
    props.isActiveDescendent &&
    css`
      ${props.theme.selectors.keyboard} & {
        ${buttonLikeFocus(props.theme)}
      }
    `}
`

export type ArcadeDatePickerDropdownProps = DropdownProps & { 'aria-describedby'?: string }

export const ArcadeDatePickerDropdown = ({
  children,
  value,
  'aria-label': ariaLabel,
  onChange,
  caption,
  'aria-describedby': ariaDescribedBy,
}: ArcadeDatePickerDropdownProps) => {
  const id = useId()
  const { state, open, close, activate } = useDropdownState(id)
  const captionPortalElement = usePortalElement()

  const options: Option[] = Children.toArray(children)
    .filter((child): child is ReactElement => isValidElement(child))
    .map(({ props }) => ({
      label: props?.children,
      value: props?.value,
    }))
    .sort(option => {
      // NOTE: This ensures the years are displayed from most recent to oldest
      const isYear = (val: string) => /^(19\d{2}|20\d{2})$/.test(val)
      return isYear(option?.label) ? -1 : 1
    })

  const selected = options.find(op => op.value === value)
  const label = selected?.label ?? String(caption)

  const select = (option?: Option) => {
    if (option) {
      close()
      onChange?.({ target: { value: option.value } } as ChangeEvent<HTMLSelectElement>)
    }
  }

  // NOTE: This is modeled after a combobox for accessibility purposes
  // https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/

  return (
    <Dropdown
      aria-describedby={ariaDescribedBy}
      onClick={() => {
        state.open ? close() : open({ on: 'selected', selected })
      }}
      onKeyDown={event => {
        switch (event.key) {
          case ' ':
          case 'Enter':
            state.open ? select(state.activeDescendent) : open({ on: 'selected', selected })
            event.preventDefault()
            event.stopPropagation()
            break
          case 'Tab':
            if (state.open) select(state.activeDescendent)
            break
          case 'ArrowUp':
          case 'ArrowLeft':
            state.open ? activate({ direction: 'previous', options }) : open({ on: 'selected', selected })
            event.preventDefault()
            break
          case 'ArrowDown':
          case 'ArrowRight':
            state.open ? activate({ direction: 'next', options }) : open({ on: 'selected', selected })
            event.preventDefault()
            break
          case 'Home':
            open({ on: 'first', options })
            event.preventDefault()
            break
          case 'End':
            open({ on: 'last', options })
            event.preventDefault()
            break
          case 'Escape':
            if (state.open) event.stopPropagation()
            close()
            break
        }
      }}
      tabIndex={0}
      role="combobox"
      aria-controls={id}
      aria-label={ariaLabel}
      aria-expanded={state.open}
      aria-haspopup="listbox"
      aria-autocomplete="none"
      aria-activedescendant={state.open && state.activeDescendent ? String(state.activeDescendent.value) : ''}
    >
      <DropdownLabelContainer>
        <DropdownLabel>{label}</DropdownLabel>
        <DropdownLabelSpacer aria-hidden>{label}</DropdownLabelSpacer>
      </DropdownLabelContainer>
      <ExpandIcon size={24} />
      {state.open &&
        captionPortalElement &&
        createPortal(
          <OptionGrid role="listbox" id={id} aria-label={label} tabIndex={-1}>
            {options.map(option => (
              <OptionElement
                key={option.value}
                id={String(option.value)}
                role="option"
                aria-selected={value === option.value}
                isActiveDescendent={state.activeDescendent?.value === option.value}
                onClick={() => select(option)}
              >
                {option.label}
              </OptionElement>
            ))}
          </OptionGrid>,
          captionPortalElement
        )}
    </Dropdown>
  )
}
